import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { DancerStatus } from '@pirouette/shared';
import { useDancers, type DancerListQuery } from '@/data/dancers';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/admin/dancers')({
  component: DancerRoster,
});

const PAGE_SIZE = 50;

function DancerRoster() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<DancerStatus | 'all'>('all');
  const [costumeDueOnly, setCostumeDueOnly] = useState(false);
  const [paymentDueOnly, setPaymentDueOnly] = useState(false);
  const [page, setPage] = useState(0);

  const query: DancerListQuery = {
    search: search || undefined,
    status,
    costumeDueOnly,
    paymentDueOnly,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const { data, isLoading, isError, error } = useDancers(query);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Roster
          </p>
          <h1 className="mt-2">Dancers</h1>
          {data && (
            <p className="mt-1 text-[var(--color-ink-muted)]">
              {data.total} {data.total === 1 ? 'dancer' : 'dancers'} matched
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Link to="/admin/dancers/import">
            <Button variant="secondary">Import CSV</Button>
          </Link>
        </div>
      </div>

      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
        }}
        className="grid gap-4 sm:grid-cols-[1fr_180px_auto] sm:items-end"
      >
        <div>
          <Label htmlFor="dancer-search">Search</Label>
          <Input
            id="dancer-search"
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="First, last, or preferred name"
            autoComplete="off"
          />
        </div>

        <div>
          <Label htmlFor="dancer-status">Status</Label>
          <select
            id="dancer-status"
            className="block h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[var(--color-ink)] focus-visible:outline-none"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as DancerStatus | 'all');
              setPage(0);
            }}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="withdrawn">Withdrawn</option>
          </select>
        </div>

        <fieldset className="flex flex-wrap items-center gap-3 self-end">
          <legend className="sr-only">Additional filters</legend>
          <label className="inline-flex items-center gap-2 text-[var(--text-sm)]">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-accent)]"
              checked={costumeDueOnly}
              onChange={(e) => {
                setCostumeDueOnly(e.target.checked);
                setPage(0);
              }}
            />
            Costume due
          </label>
          <label className="inline-flex items-center gap-2 text-[var(--text-sm)]">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-accent)]"
              checked={paymentDueOnly}
              onChange={(e) => {
                setPaymentDueOnly(e.target.checked);
                setPage(0);
              }}
            />
            Payment due
          </label>
        </fieldset>
      </form>

      {isLoading && (
        <p className="text-[var(--color-ink-subtle)]" aria-live="polite">
          Loading…
        </p>
      )}

      {isError && (
        <p role="alert" className="text-[var(--color-danger)]">
          {error instanceof Error ? error.message : 'Failed to load dancers.'}
        </p>
      )}

      {data?.rows.length === 0 && !isLoading && (
        <Empty
          title="No dancers match the filters"
          description="Try clearing the search or status filters. Dancers will appear here after a family is imported or added."
        />
      )}

      {data && data.rows.length > 0 && (
        <>
          <div role="region" aria-label="Dancers table" className="surface overflow-hidden">
            <table className="w-full text-left">
              <caption className="sr-only">Filtered dancers</caption>
              <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    DOB
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Pronouns
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Flags
                  </th>
                  <th scope="col" className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.rows.map((d) => (
                  <tr
                    key={d.id}
                    className="border-t border-[var(--color-border)] hover:bg-[var(--color-surface-sunken)]"
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium">
                        {d.preferred_name ?? d.first_name} {d.last_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)] tabular-nums">
                      {d.date_of_birth}
                    </td>
                    <td className="px-4 py-3 text-[var(--color-ink-muted)]">{d.pronouns ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge tone={d.status === 'active' ? 'success' : 'neutral'}>
                          {d.status}
                        </Badge>
                        {d.costume_due && <Badge tone="warn">costume</Badge>}
                        {d.payment_due && <Badge tone="danger">payment</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to="/admin/families/$id"
                        params={{ id: d.family_id }}
                        className="text-[var(--color-accent)] underline-offset-2 hover:underline"
                      >
                        Family
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <nav
              aria-label="Pagination"
              className="flex items-center justify-between gap-4 text-[var(--text-sm)]"
            >
              <span className="text-[var(--color-ink-muted)]">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
